import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ArtifactForm } from "../../components/ArtifactTable";
import { Plus, Sparkles, X } from "lucide-react";

const CR_FIELDS = [
  { key: "title", label: "Title", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "type", label: "Type", type: "select", options: ["Scope", "Schedule", "Cost", "Quality", "Process", "Tooling", "Organizational", "Risk", "Other"] },
  { key: "priority", label: "Priority", type: "select", options: ["Critical", "High", "Medium", "Low"] },
  { key: "businessJustification", label: "Business Justification", type: "textarea" },
  { key: "proposedSolution", label: "Proposed Solution", type: "textarea" },
  { key: "scopeImpact", label: "Scope Impact", type: "select", options: ["High", "Medium", "Low", "None"] },
  { key: "scheduleImpact", label: "Schedule Impact", type: "select", options: ["High", "Medium", "Low", "None"] },
  { key: "costImpact", label: "Cost Impact", type: "select", options: ["High", "Medium", "Low", "None"] },
  { key: "peopleImpact", label: "People Impact", type: "select", options: ["High", "Medium", "Low", "None"] },
  { key: "estimatedScheduleDaysImpact", label: "Est. Schedule Days", type: "number" },
  { key: "estimatedCostImpact", label: "Est. Cost Impact ($)", type: "number" },
];

const STATUSES = ["Draft", "Submitted", "UnderAssessment", "PendingApproval", "Approved", "Rejected", "Deferred", "Implementing", "Implemented", "Closed"];

export default function ChangeManagement() {
  const { projectId } = useParams();
  const [crs, setCrs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/change-requests`);
    setCrs(data);
    setSelected((s) => (s ? data.find((c) => c.id === s.id) || null : null));
  }, [projectId]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const save = async () => {
    try {
      if (editing) await api.patch(`/projects/${projectId}/change-requests/${editing.id}`, form);
      else await api.post(`/projects/${projectId}/change-requests`, form);
      setOpen(false); load(); toast.success("Change request saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setStatus = async (cr, status) => {
    try { await api.patch(`/projects/${projectId}/change-requests/${cr.id}`, { status }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const suggestImpacts = async (cr) => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/generate`, {
        generationType: "changeimpact",
        context: { changeRequest: { title: cr.title, description: cr.description, type: cr.type, priority: cr.priority, proposedSolution: cr.proposedSolution } },
      });
      const rows = Array.isArray(data.result) ? data.result : [];
      for (const r of rows) {
        await api.post(`/projects/${projectId}/change-requests/${cr.id}/impacts`, r);
      }
      toast.success(`${rows.length} AI stakeholder impacts added`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setAiBusy(false);
  };

  return (
    <div data-testid="change-management-page" className="flex gap-4">
      <div className="flex-1 min-w-0">
        <PageHeader overline="Change Management" title="Change Requests"
          actions={<Button data-testid="add-cr-btn" className="rounded-none" onClick={() => { setEditing(null); setForm({ type: "Scope", priority: "Medium" }); setOpen(true); }}><Plus className="h-4 w-4 mr-1" />New CR</Button>} />
        <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-200px)]">
          <table className="w-full data-table text-sm">
            <thead><tr><th>CR #</th><th>Title</th><th>Type</th><th>Priority</th><th>Status</th><th>Impacts</th></tr></thead>
            <tbody>
              {crs.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No change requests.</td></tr>}
              {crs.map((cr) => (
                <tr key={cr.id} data-testid="cr-row" onClick={() => setSelected(cr)}
                  className={`cursor-pointer ${selected?.id === cr.id ? "!bg-[#1E6FE8]/5" : ""}`}>
                  <td className="font-mono2 text-xs whitespace-nowrap">{cr.crNumber}</td>
                  <td className="font-medium">{cr.title}</td>
                  <td><StatusBadge value={cr.type} tone="blue" /></td>
                  <td><StatusBadge value={cr.priority} /></td>
                  <td><StatusBadge value={cr.status} /></td>
                  <td>{(cr.stakeholderImpacts || []).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div data-testid="cr-detail-panel" className="w-[400px] shrink-0 border border-border bg-card p-4 max-h-[calc(100vh-140px)] overflow-y-auto sticky top-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline-label">{selected.crNumber}</div>
              <h3 className="font-display font-bold text-lg tracking-tight leading-snug">{selected.title}</h3>
            </div>
            <button onClick={() => setSelected(null)} className="p-1 hover:text-red-600"><X className="h-4 w-4" /></button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">{selected.description}</p>

          <div className="mt-3">
            <label className="overline-label">Workflow status</label>
            <select data-testid="cr-status-select" className="w-full border border-input px-2 py-1.5 text-sm bg-background mt-1"
              value={selected.status} onChange={(e) => setStatus(selected, e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            {[["Scope", selected.scopeImpact], ["Schedule", selected.scheduleImpact], ["Cost", selected.costImpact], ["People", selected.peopleImpact]].map(([l, v]) => (
              <div key={l} className="border border-border p-2">
                <div className="text-muted-foreground uppercase tracking-wide text-[10px]">{l} impact</div>
                <StatusBadge value={v} />
              </div>
            ))}
          </div>
          {selected.businessJustification && (
            <div className="mt-3"><div className="overline-label">Justification</div><p className="text-sm mt-1">{selected.businessJustification}</p></div>
          )}
          {selected.proposedSolution && (
            <div className="mt-3"><div className="overline-label">Proposed solution</div><p className="text-sm mt-1">{selected.proposedSolution}</p></div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="overline-label">Stakeholder impacts</div>
            <Button data-testid="ai-suggest-impacts-btn" size="sm" variant="outline" className="rounded-none border-[#1E6FE8] text-[#1E6FE8] h-7 text-xs"
              onClick={() => suggestImpacts(selected)} disabled={aiBusy}>
              <Sparkles className="h-3 w-3 mr-1" />{aiBusy ? "Working…" : "AI suggest"}
            </Button>
          </div>
          <div className="space-y-2 mt-2">
            {(selected.stakeholderImpacts || []).map((imp) => (
              <div key={imp.id} className="border border-border p-2.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{imp.stakeholderGroupName}</span>
                  <StatusBadge value={imp.impactLevel} />
                </div>
                <p className="text-muted-foreground mt-1">{imp.changeImpactDescription}</p>
                {imp.interventionAction && <p className="mt-1"><span className="text-muted-foreground">Intervention:</span> {imp.interventionAction}</p>}
                {imp.adkarFocus && <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-[#1E6FE8]">ADKAR: {imp.adkarFocus}</span>}
              </div>
            ))}
            {(selected.stakeholderImpacts || []).length === 0 && <div className="text-xs text-muted-foreground">No impacts recorded yet.</div>}
          </div>

          <div className="mt-4">
            <Button data-testid="edit-cr-btn" variant="outline" size="sm" className="rounded-none w-full"
              onClick={() => { setEditing(selected); setForm(selected); setOpen(true); }}>Edit change request</Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? `Edit ${editing.crNumber}` : "New Change Request"}</DialogTitle></DialogHeader>
          <ArtifactForm fields={CR_FIELDS} value={form} onChange={setForm} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-cr-btn" className="rounded-none" onClick={save}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
